import { Suspense, useCallback, useEffect, useState } from "react";
import { AuthFrame } from "./components/AuthFrame.js";
import { authStateForError, type AuthState } from "./lib/auth.js";
import type { MatterSummary } from "./lib/api.js";
import type { AppScreen, DashboardView } from "./lib/navigation.js";
import { routeForPath } from "./lib/routing.js";
import { useMatters } from "./lib/use-matters.js";
import { useRouteSync } from "./lib/use-route-sync.js";
import { useThreadRoute } from "./lib/use-thread-route.js";
import { ThemeProvider } from "./lib/theme.js";
import { workspaceContextKey } from "./lib/workspace-context.js";
import { SignIn } from "./screens/SignIn.js";
import {
  AppShell,
  Billing,
  Dashboard,
  Onboarding,
  Provisioning,
  Settings,
  Team,
  Toaster,
  Workspace,
} from "./lib/lazy-screens.js";

const ContentLoading = (): React.JSX.Element => (
  <div className="flex h-full min-h-64 items-center justify-center gap-3 text-sm text-[var(--muted)]">
    <span className="loading-spinner" /> Loading…
  </div>
);

const sidebarInitiallyOpen = (): boolean =>
  !globalThis.matchMedia?.("(max-width: 767px)").matches;

const AppContent = (): React.JSX.Element => {
  const [auth, setAuth] = useState<AuthState>("checking");
  const initialRoute = routeForPath(globalThis.location.pathname);
  const [screen, setScreen] = useState<AppScreen>(initialRoute.screen);
  const [dashboardView, setDashboardView] = useState<DashboardView>(
    initialRoute.dashboardView,
  );
  const [routeMatterId, setRouteMatterId] = useState<string | null>(
    initialRoute.matterId,
  );
  const thread = useThreadRoute(initialRoute.threadId);
  const [sidebarOpen, setSidebarOpen] = useState(sidebarInitiallyOpen);
  const {
    matters,
    loading: mattersLoading,
    error: mattersError,
    setMatters,
  } = useMatters(auth === "signed_in");
  const selectedMatter = matters.find(({ id }) => id === routeMatterId) ?? null;
  const authError = new URLSearchParams(globalThis.location.search).get("auth_error");

  const checkAuth = useCallback(async (): Promise<void> => {
    setAuth("checking");
    try {
      const { getUsage } = await import("./lib/api.js");
      await getUsage();
      setAuth("signed_in");
    } catch (error) {
      setAuth(authStateForError(error));
    }
  }, []);

  useEffect(() => {
    void checkAuth();
  }, [checkAuth]);

  const toggleSidebar = useCallback((): void => {
    setSidebarOpen((current) => !current);
  }, []);

  useRouteSync({
    screen,
    dashboardView,
    matterId: routeMatterId,
    threadId: thread.threadId,
    onPopState: (route) => {
      setScreen(route.screen);
      setDashboardView(route.dashboardView);
      setRouteMatterId(route.matterId);
      thread.openThread(route.threadId);
    },
  });

  const closeMobileSidebar = (): void => {
    if (globalThis.matchMedia?.("(max-width: 767px)").matches) {
      setSidebarOpen(false);
    }
  };

  const showDashboard = (view: DashboardView): void => {
    setScreen("dashboard");
    setDashboardView(view);
    closeMobileSidebar();
  };

  const startDraft = (): void => {
    setRouteMatterId(null);
    thread.openThread(null);
    setScreen("workspace");
    closeMobileSidebar();
  };

  const openThread = (threadId: string): void => {
    thread.openThread(threadId);
    setScreen("workspace");
    closeMobileSidebar();
  };

  const openMatter = (matter: MatterSummary): void => {
    setRouteMatterId(matter.id);
    thread.openThread(null);
    setScreen("workspace");
    closeMobileSidebar();
  };

  const openCreatedMatter = (matter: MatterSummary): void => {
    setMatters((current) => [matter, ...current.filter(({ id }) => id !== matter.id)]);
    openMatter(matter);
  };

  const updateOpenMatter = (matter: MatterSummary): void => {
    setMatters((current) => [
      matter,
      ...current.filter((candidate) => candidate.id !== matter.id),
    ]);
  };

  const closeArchivedMatter = (matterId: string): void => {
    setMatters((current) => current.filter((matter) => matter.id !== matterId));
    setRouteMatterId(null);
    thread.openThread(null);
    setScreen("dashboard");
    setDashboardView("matters");
  };

  const signOut = async (): Promise<void> => {
    try {
      const { endSession } = await import("./lib/api.js");
      const result = await endSession();
      setMatters([]);
      setRouteMatterId(null);
      thread.openThread(null);
      setAuth("signed_out");
      globalThis.location.assign(result.logoutUrl);
    } catch {
      setAuth("unavailable");
    }
  };

  if (auth === "checking") {
    return (
      <AuthFrame
        eyebrow="Secure workspace"
        title="Opening Vakilat…"
        description="Checking the encrypted session and your isolated practice workspace."
      >
        <div className="flex items-center gap-3 text-sm text-[var(--muted)]">
          <span className="loading-spinner" />
          Verifying access
        </div>
      </AuthFrame>
    );
  }
  if (auth === "signed_out") {
    return <SignIn error={authError} />;
  }
  if (auth === "onboarding") {
    return (
      <Suspense fallback={<ContentLoading />}>
        <Onboarding
          onComplete={() => setAuth("signed_in")}
          onProvisioning={() => setAuth("provisioning")}
        />
      </Suspense>
    );
  }
  if (auth === "provisioning") {
    return (
      <Suspense fallback={<ContentLoading />}>
        <Provisioning
          onReady={() => setAuth("signed_in")}
          onSignedOut={() => setAuth("signed_out")}
        />
      </Suspense>
    );
  }
  if (auth === "unavailable") {
    return (
      <AuthFrame
        eyebrow="Service connection"
        title="Vakilat could not reach your workspace."
        description="Your session has not been discarded. This can be a temporary API or network problem."
      >
        <button
          type="button"
          className="primary-button w-full"
          onClick={() => void checkAuth()}
        >
          Try again
        </button>
      </AuthFrame>
    );
  }

  return (
    <Suspense fallback={<ContentLoading />}>
      <AppShell
        sidebarOpen={sidebarOpen}
        screen={screen}
        dashboardView={dashboardView}
        matters={matters}
        selectedMatterId={selectedMatter?.id ?? null}
        onSidebarToggle={toggleSidebar}
        onOverview={() => showDashboard("overview")}
        onMatters={() => showDashboard("matters")}
        onNewDraft={startDraft}
        onBilling={() => {
          setScreen("billing");
          closeMobileSidebar();
        }}
        onSettings={() => {
          setScreen("settings");
          closeMobileSidebar();
        }}
        onTeam={() => {
          setScreen("team");
          closeMobileSidebar();
        }}
        onMatter={openMatter}
        activeThreadId={thread.threadId}
        onOpenThread={openThread}
        onSignOut={() => void signOut()}
      >
        {screen === "dashboard" ? (
          <Dashboard
            view={dashboardView}
            matters={matters}
            mattersLoading={mattersLoading}
            mattersError={mattersError}
            onStartDraft={startDraft}
            onOpenMatter={openMatter}
            onMatterCreated={openCreatedMatter}
            onShowMatters={() => showDashboard("matters")}
          />
        ) : screen === "workspace" ? (
          <Workspace
            key={`${workspaceContextKey(selectedMatter, thread.openedThreadId)}:${String(thread.navigationRevision)}`}
            matter={selectedMatter}
            threadId={thread.openedThreadId}
            onThreadCreated={thread.threadCreated}
            onMatterUpdated={updateOpenMatter}
            onMatterArchived={closeArchivedMatter}
          />
        ) : screen === "billing" ? (
          <Billing />
        ) : screen === "settings" ? (
          <Settings />
        ) : (
          <Team />
        )}
      </AppShell>
    </Suspense>
  );
};

export const App = (): React.JSX.Element => (
  <ThemeProvider>
    <AppContent />
    {/* Inside the provider: the toaster reads our theme, not next-themes. */}
    <Suspense fallback={null}>
      <Toaster position="bottom-right" richColors closeButton />
    </Suspense>
  </ThemeProvider>
);
